import React, { useState, useEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import RadioCheck from '../../../components/MessageManager/RadioCheck'
import { SignageZonePanel, AddSignageButton } from './zonePanelsConfig'
import commonFunctions from '../../../utils/commonFunctions'
import { SIGNAGE_MEDIA, WIDGET_SIZE_TYPE } from '../../../utils/constants'
import API from '../../../utils/API'
import { fetchAndParseICS, getWeatherWidget, getCalendarWidget, getDigitalClockWidget, getAnalogClockWidget, updateAnalogClock } from './PreviewContent/widget';
import { UpdatePlaylistContent, clearUpdatePlaylistContent } from './PreviewContent/UpdatePlaylistContent';

const SignageMainConfigPanel = ({
  signageName,
  setSignageName,
  signageNameValid,
  zoneType,
  setZoneType,
  zoneOptions,
  zonePanelsConfig,
  validated,
  zone,
  setZone,
  zoneData,
  setPlayListLengthValid,
  intl,
  markUnsavedChanges
}) => {
  const panelRefs = useRef({});

  const getPlaylistContent = async (id) => {
    const playlist = await API.getPlaylistItems(id);
    
    playlist.data.items.forEach((item)=>{
      if (item.content.isRejectIframe) item.content.rejectContent = intl.formatMessage({ id: "PREVIEW_NOT_AVAILABLE" });
    });

    return playlist;
  }

  function adjustScrollDuration(container) {
    const defaultDuration = 25;
    let divLength = 0;
    let boxwidth = document.getElementById("signage-content-ticker").getBoundingClientRect().width;
    const marginRight = boxwidth / 5;
    const items = document.getElementsByClassName("ticker-text-scroll-item");
    for (const [index, item] of Array.from(items).entries()) {
      if(index < items.length - 1)
        item.setAttribute("style", `margin-right: ${marginRight}px;`);
      divLength += item.getBoundingClientRect().width + (index < item.length -1 ? marginRight : 0);
    }
    const ratio = divLength / boxwidth;
    const duration = ratio < 1 ? defaultDuration : parseInt((ratio + 1) * defaultDuration);
    container.setAttribute("style", `animation-duration: ${duration}s;`);
  }

  useEffect(() => {
    Object.keys(panelRefs.current).forEach((key) => {
      if (!zonePanelsConfig[zoneType]?.some((colConfig) => colConfig.panels.some((panel) => panel.zone === key))) {
        delete panelRefs.current[key];
      }
    });

    const adjustPaddingBottom = () => {
      let signageMainPanel = document.querySelector('.signage-main-panel');
      let col9Div = signageMainPanel?.querySelector('.col-9');
      let panelContainer = signageMainPanel?.querySelector('.col-9 .panel-container');
      let col3Div = signageMainPanel?.querySelector('.col-3');
      if (zoneType === "10") {
        signageMainPanel.style.paddingBottom = `20px`;
        signageMainPanel.querySelector('.col-12').style.maxHeight = '100%';
        return;
      };
      let col9Height = col9Div.offsetWidth / (16 / 9);
      if (signageMainPanel && col9Div) {
        let width = signageMainPanel.offsetWidth;
        let idealHeight = width / (16 / 9);
        panelContainer.style.maxHeight = `${col9Height}px`;
        let paddingBottom = Math.max(0, idealHeight - col9Height);

        if (zoneType === "40" || zoneType === "32") {
          paddingBottom = paddingBottom - 100;
        }
        signageMainPanel.style.paddingBottom = `${paddingBottom}px`;
      }
      if (col3Div) {
        col3Div.style.maxHeight = `${col9Height}px`;
      }
    };

    adjustPaddingBottom();
    window.addEventListener('resize', adjustPaddingBottom);
    return () => {
      window.removeEventListener('resize', adjustPaddingBottom);
    };
  }, [zoneType]);

  useEffect(() => {
    const tickerElement = panelRefs.current[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER];
    if (!tickerElement || !zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER]) return;

    const content = zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER]?.content;
    if(!content.message) {
      console.log('content.message is null');
      return;
    }

    const htmlContent = [];
    htmlContent.push(`<div class="ticker-text-scroll">`);
    let index = 0;
    content.message.forEach(item => {
      if(!item || item.trim() === '')
        return;

      htmlContent.push(`<div class="ticker-text-scroll-item">${item.trim()}</div>`);
      index++;
    });
    htmlContent.push(`</div>`);
    tickerElement.innerHTML = htmlContent.join('');
    adjustScrollDuration(document.getElementsByClassName("ticker-text-scroll")[0]);
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER], zoneType]);

  const widgetTimers = {};
  const resizeHandlers = {};

  const handleWidgetUpdate = async (zone) => {
    const container = panelRefs.current[zone];
    if (!container || !zoneData[zone]) return;

    const content = zoneData[zone]?.content;
    const subcategory = content.subcategory;

    if (resizeHandlers[zone]) {
      window.removeEventListener('resize', resizeHandlers[zone]);
      delete resizeHandlers[zone];
    }

    switch (subcategory) {
      case SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER:
        const weatherResizeHandler = async () => {
          if (!container || !content || !content.paramters) return;
          let widgetSize = WIDGET_SIZE_TYPE.SMALL;
          if (zoneType === "20" || zoneType === "32") {
            widgetSize = WIDGET_SIZE_TYPE.MEDIUM;
          }
          container.innerHTML = await getWeatherWidget(content.paramters, widgetSize);
          const containerHeight = container.offsetHeight;
          if (containerHeight <= 0) return;

          const weatherMainInfoDiv = container.querySelector('.weather-main-info');
          if (!weatherMainInfoDiv) return;

          if (containerHeight < weatherMainInfoDiv.offsetHeight) {
            let scale = containerHeight / weatherMainInfoDiv.offsetHeight;
            if (scale < 1) {
              weatherMainInfoDiv.style.transform = `scale(${scale})`;
            }
          }
        }
        weatherResizeHandler();
        resizeHandlers[zone] = weatherResizeHandler;
        window.addEventListener('resize', resizeHandlers[zone]);
        break;
      case SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CLOCK:
        container.innerHTML = '';
        if (content.paramters?.type === SIGNAGE_MEDIA.MEDIA_TYPE.TYPE_ANALOG) {
          container.style.background = "linear-gradient(0deg, #171719, #171719),linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(24, 24, 26, 0.2) 100%, rgba(24, 24, 26, 0.2) 100%)";
          container.style.alignContent = "center";

          await getAnalogClockWidget(subcategory + zone, container, content.paramters);
          widgetTimers[zone] = setInterval(() => {
            updateAnalogClock(subcategory + zone, content.paramters);
          }, 1000);
        } else {
          container.style.background = undefined;
          container.style.alignContent = undefined;

          const digitalClockResizeHandler = async () => {
            container.innerHTML = '';
            await getDigitalClockWidget(subcategory + zone, container, content.paramters);
            const digitalClockMainInfoDiv = container.querySelector('.clock-main-info');
            if (container.offsetWidth < 258) {
              let scale = container.offsetWidth / 258;
              if (digitalClockMainInfoDiv && scale < 1) {
                digitalClockMainInfoDiv.style.transform = `scale(${scale})`;
                digitalClockMainInfoDiv.style.transformOrigin = 'center left';
              }
            }
          };
          digitalClockResizeHandler();
          resizeHandlers[zone] = digitalClockResizeHandler;
          window.addEventListener('resize', digitalClockResizeHandler);
        }
        break;
      case SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR:
        const calendarResizeHandler = async () => {
          container.innerHTML = '';
          if (content && content.paramters && content.paramters.calendarType === 0) {
            await getCalendarWidget(subcategory + zone, container, content.paramters);
            const calendarMainInfoDiv = container.querySelector('.calendar-main-info');
            if (container.offsetHeight < calendarMainInfoDiv.offsetHeight) {
              let scale = container.offsetHeight / calendarMainInfoDiv.offsetHeight;

              if (calendarMainInfoDiv && scale < 1) {
                calendarMainInfoDiv.style.transform = `scale(${scale})`;
              }
            }
          } else if (content && content.paramters && content.paramters.calendarType === 1) {
            if (content.paramters.calendarUrl) {
              const calendarHTML = await fetchAndParseICS(content.paramters);
              if (calendarHTML) {
                container.innerHTML = '';              
                container.appendChild(calendarHTML);
                const calendarMainInfoDiv = container.querySelector('.outer-container');
                if (container.offsetWidth < calendarMainInfoDiv.offsetWidth) {
                  let scale = container.offsetWidth / calendarMainInfoDiv.offsetWidth;

                  if (calendarMainInfoDiv && scale < 1) {
                    calendarMainInfoDiv.style.transform = `scale(${scale})`;
                    calendarMainInfoDiv.style.transformOrigin = 'top left';
                  }
                }
              }
            }
          }
        };
        calendarResizeHandler();
        resizeHandlers[zone] = calendarResizeHandler;
        window.addEventListener('resize', resizeHandlers[zone]);
        break;
      default:
        container.innerHTML = `<h4>${content.name}</h4><p>${content.description || "No description available"}</p>`;
        break;
    }
  };

  useEffect(() => {

    handleWidgetUpdate(SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET1);
    return () => {
      Object.values(widgetTimers).forEach(clearInterval);
      Object.keys(resizeHandlers).forEach((zone) => {
        window.removeEventListener('resize', resizeHandlers[zone]);
      });
    };
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET1], zoneType]);

  useEffect(() => {
    const widgetTimers = {};
    const resizeHandlers = {};
    handleWidgetUpdate(SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET2);
    return () => {
      Object.values(widgetTimers).forEach(clearInterval);
      Object.keys(resizeHandlers).forEach((zone) => {
        window.removeEventListener('resize', resizeHandlers[zone]);
      });
    };
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET2], zoneType]);

  useEffect(() => {
    const container = panelRefs.current[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST];
    if (!container || !zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST]) return;

    const content = zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST]?.content;
    let items = content;
    let isLoop = zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].isLoop;

    const updatePlaylist = async () => {
      if (!items && zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].playlistId) {
        const response = await getPlaylistContent(zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].playlistId);
        items = response.data.items;
        isLoop = response.data.isLoop;
        /**
         * Mei 2026-1-22
         * Frontend: send none content of Playlist to backend when creating a new signage or updating signage(after switch playList)
         * Backend: The empty playlist content will cause missing add weather widget city to scheduler job(update weather info every hour)
         */        
        zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST].content = items;
      }
      items = items || [];
      setPlayListLengthValid(items.length);
      await UpdatePlaylistContent(items, container, isLoop);
    };

    updatePlaylist();
  }, [zoneData[SIGNAGE_MEDIA.CREATE_SIGNAGE.PLAYLIST], zoneType]);

  /**
   * Mei 2026-1-7 Cleanup on component unmount
   * fix issues: The preview playlist video/audio keep playing after user leave the CreateSignage preview page
   */
  useEffect(() => {
    return () => {
      //Cleanup operations during component uninstallation
      clearUpdatePlaylistContent();
      console.log('The component has been uninstalled; performing cleanup.');
     
    };
  }, []); //An empty dependency array means it will only run during mounting and unmounting.

  return (
    <>
      <div className="mb-3">
        <label className="font-weight-600 font-size-14 mb-2" htmlFor="signageNameInput">
          <FormattedMessage id="SIGNAGE_NAME" defaultMessage="Signage Name" />:
        </label>
        <input
          id="signageNameInput"
          className={`form-control h-54 bg-baby-blue ${validated && (!signageNameValid ? 'is-valid' : 'is-invalid')}`}
          value={signageName}
          onChange={e => { setSignageName(e.target.value); markUnsavedChanges && markUnsavedChanges(); }}
          placeholder={intl.formatMessage({ id: "TYPE_HERE" })}
        />
        {commonFunctions.InvalidFeedbackString(signageNameValid, "SIGNAGE_NAME")}
      </div>
      <div className="mb-3">
        <label className="font-weight-600 font-size-14 mb-2">
          <FormattedMessage id="SIGNAGE_ZONE" defaultMessage="Signage Zone" />:
        </label>
        <RadioCheck
          options={zoneOptions}
          name="SIGNAGE_ZONE"
          selectedOption={zoneType}
          onChange={e => { setZoneType(e.target.value); markUnsavedChanges && markUnsavedChanges(); }}
        />
      </div>
      <div
        className="signage-main-panel border rounded "
        style={{
          padding: '20px 16px',
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          height: '100%',
        }}
      >
        {zonePanelsConfig[zoneType] ? (
          <>
            {zonePanelsConfig[zoneType].map((colConfig, colIdx) => (
              <div
                key={colIdx}
                className={`col-${colConfig.col}${colConfig.className ? ' ' + colConfig.className : ''}`}
                style={
                  colConfig.style
                }
              >
                {colConfig.panels.map((panel, idx) => (
                  <div
                    key={idx}
                    className={`panel-container ${panel.panelClassName} ${panel.zone === 'ticker' ? 'ticker-text' : ''}`}
                    style={{ ...panel.panelStyle, alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}
                  >
                    {!zoneData || (zoneData && !zoneData[panel.zone]) ? (
                      <SignageZonePanel style={{ background: "#F7FAFD" }}>
                        {panel.title && <div className="text-center fw-bold mb-2">{panel.title}</div>}
                        <AddSignageButton
                          label={panel.button}
                          {...panel.buttonProps}
                          onClick={() => setZone(panel.zone)}
                        />
                      </SignageZonePanel>
                    ) : (
                      <>
                        <div id={'signage-content-' + panel.zone}
                          ref={(el) => (panelRefs.current[panel.zone] = el)}
                          className="w-100 h-100 text-left" style={{
                            ...(panel.zone !== SIGNAGE_MEDIA.CREATE_SIGNAGE.TICKER &&{
                              backgroundColor: "#F7FAFD",
                            }),
                            borderRadius: "12px",
                            overflow: "hidden",
                          }}
                        />
                        <div className="overlay position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
                          <button className="btn btn-custom" {...panel.buttonProps} onClick={() => setZone(panel.zone)}>
                            {panel.replaceButton}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </>
        ) : null}
      </div>
    </>
  )
};

export default SignageMainConfigPanel;
